import { motion } from "framer-motion";
import { FileText, Building2, Landmark, PiggyBank, TrendingUp, Receipt } from "lucide-react";

const SERVICES = [
  {
    icon: FileText,
    title: "Tax Returns & Planning",
    desc: "Strategic, forward-looking tax structuring and precise returns for individuals and entities.",
  },
  {
    icon: Building2,
    title: "Business Accounting",
    desc: "Bookkeeping, financial statements and management reporting that keep you ahead.",
  },
  {
    icon: Landmark,
    title: "ASIC Services",
    desc: "Company registrations, ongoing ASIC compliance and registered agent representation.",
  },
  {
    icon: PiggyBank,
    title: "SMSF",
    desc: "Establishment, administration and compliance for Self-Managed Super Funds.",
  },
  {
    icon: TrendingUp,
    title: "Business Advisory",
    desc: "Growth strategy, cash-flow modelling and structuring for long-term wealth.",
  },
  {
    icon: Receipt,
    title: "BAS & GST",
    desc: "Accurate, on-time Business Activity Statements and GST management.",
  },
];

const card = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.1 },
  }),
};

export const Services = () => {
  return (
    <section id="services" className="relative py-24 md:py-40 bg-[#0c0c12]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-4 mb-16">
          <span className="font-body text-xs tracking-[0.3em] uppercase text-[#c9a84c]">02 — Services</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-display font-light tracking-tight text-[#f8f9fa] text-3xl sm:text-4xl lg:text-5xl max-w-3xl leading-tight mb-20"
        >
          A complete practice, held to a private-office standard.
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={card}
                data-testid={`service-card-${i}`}
                className="group relative p-8 lg:p-10 bg-[#12121a] border border-white/5 hover:border-[#c9a84c]/40 transition-colors duration-500 overflow-hidden"
              >
                <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-[#c9a84c]/0 group-hover:bg-[#c9a84c]/10 blur-3xl transition-colors duration-700" />
                <div className="relative">
                  <div className="h-14 w-14 grid place-items-center border border-[#c9a84c]/30 rounded-full mb-8 group-hover:bg-[#c9a84c] transition-colors duration-500">
                    <Icon size={22} className="text-[#c9a84c] group-hover:text-[#0a0a0f] transition-colors duration-500" strokeWidth={1.4} />
                  </div>
                  <h3 className="font-display text-xl lg:text-2xl text-[#f8f9fa] font-medium mb-4">
                    {s.title}
                  </h3>
                  <p className="font-body text-sm text-[#9ba1a6] font-light leading-relaxed">
                    {s.desc}
                  </p>
                </div>
                <span className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full bg-[#c9a84c] transition-[width] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
